import differenceInYears from 'date-fns/differenceInYears';
import { addDays, isFriday, parse, isWithinInterval, subDays, isBefore, isSameDay } from 'date-fns';
import stringSimilarity from 'string-similarity';
import i18n from '../i18n';
import { dateStringFormat, oneDayMs, months, FREELANCE, FULLTIME, CONTRACT } from '../constants/general';

// ratio threshold to consider as similar text
const SIMILARITY_RATIO = 0.4;

// date helper functions

export const shouldDisplayIsWeekend = (date) =>
    isFriday(parse(date, dateStringFormat, new Date()));

export const getMonthName = date => {
  return months[date.getMonth()];
};

export const generateFormattedDateHeaders = (startOfSelectedWeek, includeWeekend) => {
  const numOfDays = includeWeekend ? 7 : 5;
  const formattedDates = [];
  let currentDate = new Date(startOfSelectedWeek.getTime()); // new Date() is required because setDate() modifies reference value

  for (let i = 0; i < numOfDays; i++) {
    const month = getMonthName(currentDate).substring(0, 3);
    const dateNumber = currentDate.getDate();
    formattedDates.push(`${month} ${dateNumber}`);
    currentDate = addDays(currentDate, 1);
  }
  return formattedDates;
}

export const getMonday = date => {
  const day = date.getUTCDay();
  let newDate = new Date(date);
  newDate.setUTCHours(0, 0, 0, 0);

  if (day > 1) {
    newDate = new Date(newDate.getTime() - (day - 1) * oneDayMs);
    return newDate;
  }
  if (day < 1) {
    newDate = new Date(newDate.getTime() + 1 * oneDayMs);
    return newDate;
  }
  return newDate;
};

export const formatDateOnlyString = date => {
  const year = date.getUTCFullYear();
  const month =
    date.getUTCMonth() + 1 > 9
      ? date.getUTCMonth() + 1
      : `0${date.getUTCMonth() + 1}`;
  const dateNumber =
    date.getUTCDate() > 9 ? date.getUTCDate() : `0${date.getUTCDate()}`;
  return `${year}-${month}-${dateNumber}`;
};

export const formatDateOnlyStringFromLocalTime = date => {
  const year = date.getFullYear();
  const month =
    date.getMonth() + 1 > 9 ? date.getMonth() + 1 : `0${date.getMonth() + 1}`;
  const dateNumber = date.getDate() > 9 ? date.getDate() : `0${date.getDate()}`;
  return `${year}-${month}-${dateNumber}`;
};

export const generateDaysOfWeekAsStrings = (startOfSelectedWeek, includeWeekend) => {
  const numOfDays = includeWeekend ? 7 : 5;
  const daysOfWeekAsStrings = [];
  let currentDate = new Date(startOfSelectedWeek.getTime()); // new Date() is required because setDate() modifies reference value
  for (let i = 0; i < numOfDays; i++) {
    daysOfWeekAsStrings.push(formatDateOnlyStringFromLocalTime(currentDate));
    currentDate = addDays(currentDate, 1);
  }
  return daysOfWeekAsStrings;
}

export const getLocalDateFromDateString = dateString => {
  const year = parseInt(dateString.substr(0, 4), 10);
  const month = parseInt(dateString.substr(5, 2), 10) - 1;
  const date = parseInt(dateString.substr(8, 2), 10);
  return new Date(year, month, date);
};

export const isBeforeToday = (dateToCompare, today = new Date())=> {
  return (isBefore(dateToCompare, today) && !isSameDay(dateToCompare, today));
};

export const formatDateForNotes = date => {
  return date.slice(0, 10);
};

export const nameToValueMap = element => {
  return {
    ...element,
    value: element.id,
    displayValue: element.name
  };
};

export const getFirstOfNextMonth = dateString => {
  const year = dateString.substr(0, 4);
  const month = parseInt(dateString.substr(5, 2), 10) + 1;
  if (month < 13) {
    const stringMonth = month < 10 ? `0${month}` : `${month}`;
    return `${year}-${stringMonth}-01`;
  }
  return `${parseInt(year, 10) + 1}-01-01`;
};

export const getFirstOfLastMonth = dateString => {
  const year = dateString.substr(0, 4);
  const month = parseInt(dateString.substr(5, 2), 10) - 1;
  if (month === 0) {
    return `${parseInt(year, 10) - 1}-12-01`;
  }
  const stringMonth = month < 10 ? `0${month}` : `${month}`;
  return `${year}-${stringMonth}-01`;
};

export const hasContractOverlaps = contracts => {
  let hasOverlaps = false;
  contracts.forEach((contract, index) => {
    contracts.forEach((contractToCompare, innerIndex) => {
      if (index !== innerIndex) {
        if (
          isWithinInterval(contract.contractStart, {
            start: contractToCompare.contractStart,
            end: subDays(contractToCompare.contractEnd, 1)
          })
        ) {
          hasOverlaps = true;
        }
        if (
          isWithinInterval(contract.contractEnd, {
            start: contractToCompare.contractStart,
            end: subDays(contractToCompare.contractEnd, 1)
          })
        ) {
          hasOverlaps = true;
        }
      }
    });
  });
  return hasOverlaps;
};

export const isValidUrl = url => {
  const regexp = new RegExp(
    '^(https?:\\/\\/)?' +
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' +
      '((\\d{1,3}\\.){3}\\d{1,3}))' +
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' +
      '(\\?[;&a-z\\d%_.~+=-]*)?' +
      '(\\#[-a-z\\d_]*)?$',
    'i'
  );
  return regexp.test(url);
};

export const isValidDate = dateStr => {
  return Number.isNaN(dateStr) && Date.parse(dateStr) > 0;
};

export const calculateAge = birthday => {
  return differenceInYears(new Date(), birthday);
};

export const similarStrings = (stringToMatch, arrayOfStrings) => {
  const similarNames = [];
  const weightedMatches = stringSimilarity.findBestMatch(
    stringToMatch,
    arrayOfStrings
  ).ratings;
  weightedMatches.forEach(weightedMatch => {
    if (weightedMatch.rating > SIMILARITY_RATIO) {
      similarNames.push(weightedMatch);
    }
  });
  similarNames.sort((a, b) => b.rating - a.rating);
  return similarNames;
};

export const isUniqueString = (value, arrayOfStrings) => {
  if (value === '') {
    return false;
  }
  let valid = true;
  arrayOfStrings.forEach(existingString => {
    if (existingString.toLowerCase() === value.toLowerCase()) {
      valid = false;
    }
  });
  return valid;
};

export const formatEmployeeDisplayName = (firstName, lastName, nickName) => {
  if (nickName) {
    return nickName;
  }
  if (firstName) {
    if (lastName) {
      return `${firstName} ${lastName}`;
    }
    return firstName;
  }
  if (lastName) {
    return lastName;
  }
  return i18n.t('noData');
};

export const groupAgents = (agencies, agents) =>
  agencies.map(agency => {
    const options = agents.filter(agent => agent.agencyId === agency.id);
    return {
      label: agency.name,
      options
    };
  });

export const groupLocations = (locationTypes, locations) =>
  locationTypes.map(locationType => {
    const options = locations.filter(location => location.locationTypeId === locationType.id);
    return {
      label: locationType.locationTypeName,
      options: options.map(option => {
        return {
          ...option,
          name: option.locationName,
          label: option.locationName,
          value: option.id,
        }
      }),
    };
  });

export const determineEmployeeType = (isFullTime, isContractor) => {
  if (!isFullTime) {
    return FREELANCE;
  }
  if (isContractor) {
    return CONTRACT;
  }
  return FULLTIME;
};

export const convertUnixTimeToDateString = unixTimestamp => {
  if (unixTimestamp === null || unixTimestamp === 0) {
    return i18n.t('notAvailable');
  }
  const date = new Date(unixTimestamp * 1000);
  return formatDateOnlyStringFromLocalTime(date);
};

export const isOverFourMonths = (unixTimestamp, today) => {
  const todayInUnixTime = today.getTime() / 1000;
  const fourMonthsInUnixTime = 10620000;

  const staleMeasurementThreshold = unixTimestamp + fourMonthsInUnixTime;

  return staleMeasurementThreshold < todayInUnixTime;
};

export const positionRequiresMeasurements = employeePositions => {
  if (employeePositions != null) {
    for (let i = 0; i < employeePositions.length; i++) {
      if (
        employeePositions[i].id === 1 ||
        employeePositions[i].id === 2 ||
        employeePositions[i].id === 3 ||
        employeePositions[i].id === 31
      ) {
        return true;
      }
    }
  }
  return false;
};

export const getItemCountFromLocation = (locations, date, dailyAssignmentKey, attributesToTally) => {
  let items = 0;
  locations.forEach(location => {
    const match = location[dailyAssignmentKey].find(
      dailyAssignment => dailyAssignment.date === date
    );
    if (match) {
      attributesToTally.forEach(attribute => {
        items += match[attribute.key] * attribute.multiplier; 
      });
    }
  });
  return items;
}

export const formatMoney = input => {
  if (!input) {
    return '$0';
  }
  const ranges = [
    { divider: 1e9, suffix: 'G' },
    { divider: 1e6, suffix: 'M' },
    { divider: 1e3, suffix: 'K' }
  ];

  for (let i = 0; i < ranges.length; i++) {
    const { divider, suffix } = ranges[i];
    if (input >= divider) {
      const value = Math.round((input / divider) * 10) / 10;
      return `$${value.toString()}${suffix}`;
    }
  }

  return `$${input.toString()}`;
};
